import LegalLayout from "../components/LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" updated="3 October 2026">
      <h2>Who we are</h2>
      <p>
        Groundwork TierUp (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is operated by Groundwork TierUp (ABN 16 442 280 390), a
        platform that helps delivery leaders connect project management, change management, and business improvement in one place.
      </p>
      <p>
        This policy explains how Groundwork TierUp collects, uses, stores and shares personal information when you use our website and
        app.
      </p>

      <h2>Information we collect</h2>
      <p>We collect the following categories of information:</p>
      <ul>
        <li><strong>Account information</strong>: name, email address, company name, job title, and password (stored securely, never in plain text).</li>
        <li><strong>Billing information</strong>: plan selected, billing address, and transaction history. Full card details are collected and stored by our payment processor, Stripe, not by us directly; we never see or store your full card number.</li>
        <li><strong>Content you create</strong>: projects, charters, change plans, and other data you input into the platform to use the service.</li>
        <li><strong>Usage data</strong>: pages visited, features used, log data, device and browser type, and IP address, collected automatically through our server logs, session cookies, Google Analytics, and the Meta Pixel (for measuring ad performance if you arrive via a Groundwork TierUp ad).</li>
        <li><strong>Communications</strong>: messages you send us by email or through our support channels.</li>
      </ul>

      <h2>How we collect it</h2>
      <p>
        Directly from you when you sign up, use the platform, contact support, or subscribe to updates; and automatically through cookies
        and analytics when you visit our site or use the app.
      </p>

      <h2>Why we use it</h2>
      <p>We use personal information to:</p>
      <ul>
        <li>provide, operate, and maintain the Groundwork TierUp platform;</li>
        <li>process subscription payments and manage your account;</li>
        <li>respond to support requests and communicate service-related updates;</li>
        <li>send product updates, tips, or marketing communications where you&rsquo;ve opted in, always with an unsubscribe option (per the Spam Act 2003);</li>
        <li>improve the platform through aggregated, de-identified usage analysis;</li>
        <li>meet legal and regulatory obligations.</li>
      </ul>

      <h2>Who we share it with</h2>
      <p>
        We don&rsquo;t sell your personal information. We use a small number of trusted third-party services to run Groundwork TierUp,
        and share only what each one needs:
      </p>
      <ul>
        <li><strong>Stripe</strong>, to process payments and manage subscriptions</li>
        <li><strong>Resend</strong>, to send account emails such as password reset links</li>
        <li><strong>Kit</strong>, to send Field Notes and other marketing emails you&rsquo;ve opted in to</li>
        <li><strong>Our AI model provider</strong> (currently Google), to generate AI Coach responses from the content you submit to it</li>
        <li><strong>Google Analytics</strong>, to understand how the site and app are used</li>
        <li><strong>Meta</strong>, via the Meta Pixel, to measure how our advertising performs</li>
        <li><strong>Our hosting and database providers</strong>, to run the platform</li>
      </ul>
      <p>
        We may also disclose information where required by law, or to a buyer if Groundwork TierUp is ever sold, on the condition that
        this policy continues to apply.
      </p>

      <h2>Where your data is stored</h2>
      <p>
        The Groundwork TierUp app, and the project and other data you enter into it, is hosted on servers in Australia. Some of the
        third-party services listed above may store or process information outside Australia, including in the United States.
      </p>

      <h2>Cookies</h2>
      <p>
        Our site and app use cookies to keep you logged in, remember your preferences, and understand how the platform and our
        advertising are performing, through Google Analytics and the Meta Pixel. You can control or disable cookies through your browser
        settings, opt out of Google Analytics specifically via the{" "}
        <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics opt-out browser add-on</a>,
        or manage how Meta uses your data for ads via your{" "}
        <a href="https://www.facebook.com/adpreferences" target="_blank" rel="noopener noreferrer">Meta ad preferences</a>; doing any of
        this may limit some site functionality.
      </p>

      <h2>Data security</h2>
      <p>
        We use industry-standard measures (including encryption in transit, access controls, and regular security review)
        to protect your information. No online system is 100% secure, and we can&rsquo;t guarantee absolute security, but we take
        reasonable steps to prevent unauthorised access, loss, or misuse.
      </p>

      <h2>Data retention</h2>
      <p>
        We keep your information while your account exists, including after a subscription ends, so you can come back without losing
        your work.
      </p>
      <p>
        You can ask us to delete your account at any time. To protect your account, we&rsquo;ll only act on a deletion request sent
        from, or confirmed from, the email address on the account. When we delete your account, we keep the emails about your request
        for 12 months as a record that we did it, then delete them too. Copies in our daily backups are overwritten within 7 days, and
        our email provider, Resend, keeps its record of emails we&rsquo;ve sent you for 30 days. Payment records, such as invoices and
        transaction history, are kept by our payment processor, Stripe, as tax and financial record-keeping laws require, even after
        your account is deleted. If you also subscribe to Field Notes, that&rsquo;s a separate list: unsubscribe from any email, or ask
        us to remove you.
      </p>

      <h2>Your choices</h2>
      <p>
        You can ask us for a copy of the personal information we hold about you, ask us to correct it, or ask us to delete it. Email{" "}
        <a href="mailto:hello@groundworktierup.com.au">hello@groundworktierup.com.au</a> and we&rsquo;ll respond within 5 business days.
      </p>

      <h2>Children&rsquo;s privacy</h2>
      <p>
        Groundwork TierUp is a business platform not directed at or intended for use by children, and we don&rsquo;t knowingly collect
        personal information from anyone under 18.
      </p>

      <h2>Questions or concerns</h2>
      <p>
        If you have a question or concern about how we&rsquo;ve handled your information, email{" "}
        <a href="mailto:hello@groundworktierup.com.au">hello@groundworktierup.com.au</a> and we&rsquo;ll respond within 5 business days.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        We may update this policy from time to time. We&rsquo;ll post the updated version here with a new &ldquo;last updated&rdquo;
        date, and for material changes, we&rsquo;ll notify active subscribers by email.
      </p>

      <h2>Contact us</h2>
      <p>
        Questions about this policy? Email <a href="mailto:hello@groundworktierup.com.au">hello@groundworktierup.com.au</a>.
      </p>
    </LegalLayout>
  );
}
